import { useState } from 'react'
import { DialogTrigger } from 'react-aria-components'
import { Outlet, useParams } from 'react-router'
import { ACCOUNT_PERMISSION } from '../../shared/constants'
import type { ObjectProperty } from '../../shared/types'
import { useHasAccountPermission } from '../account-permissions'
import {
	useObjectPropertyList,
	useObjectPropertyRemove,
	ObjectPropertiesParamsType,
} from '../api/object-properties'
import { Button, ButtonLink } from '../components/button'
import { Input } from '../components/input'
import { Paper } from '../components/paper'
import { Sidepanel, SidepanelContent, SidepanelFooter } from '../components/sidepanel'
import { List, useList } from '../scaffolding/list'
import { ListHeader } from '../scaffolding/list-header'

type Filter = Pick<ObjectPropertiesParamsType, 'id' | 'name' | 'identifier'>

export function ObjectProperties({ kind }: { kind?: string }) {
	const { object = kind } = useParams<{ object: string }>()
	if (!object) throw new Error('Invalid invocation')
	const [params, setParams] = useList()
	const [filter, setFilter] = useState<Filter>({})

	const query = useObjectPropertyList({ ...params, ...filter, object })
	const remove = useObjectPropertyRemove()
	const canEditObjectProperties = useHasAccountPermission(
		ACCOUNT_PERMISSION.EDIT_OBJECT_PROPERTIES,
	)
	const canDeleteObjectProperties = useHasAccountPermission(
		ACCOUNT_PERMISSION.DELETE_OBJECT_PROPERTIES,
	)

	const onRemove = (id: string) => {
		if (confirm('Opravdu chcete smazat vlastnost?')) {
			remove.mutate(id)
		}
	}

	return (
		<Paper>
			<div className="flex justify-between gap-4 px-5 py-4 sm:px-6 sm:py-5">
				<h3 className="text-md font-bold">Seznam vlastností</h3>
				<DialogTrigger>
					<Button variant="SECONDARY">Filrovat</Button>
					<Sidepanel heading="Filtrovat">
						{({ close }) => (
							<ObjectPropertiesSidepanel
								onClose={close}
								filter={filter}
								onSubmit={setFilter}
							/>
						)}
					</Sidepanel>
				</DialogTrigger>
			</div>
			<List<ObjectProperty>
				params={params}
				setParams={setParams}
				query={query}
				header={
					<tr>
						<ListHeader sortBy="id">ID</ListHeader>
						<ListHeader sortBy="name">Název</ListHeader>
						<ListHeader sortBy="type">Typ</ListHeader>
						<ListHeader sortBy="identifier">Klíč</ListHeader>
						<th>Popis</th>
						<th></th>
					</tr>
				}
			>
				{objectProperty => (
					<tr key={objectProperty.id}>
						<td width="8%">{objectProperty.id}</td>
						<td width="12%">{objectProperty.name}</td>
						<td width="10%">{objectProperty.type}</td>
						<td width="10%">{objectProperty.identifier}</td>
						<td width="40%">{objectProperty.description}</td>
						<td width="20%" className="space-x-2">
							{objectProperty.isManaged ? (
								<small>Systémová vlastnost</small>
							) : (
								<>
									<ButtonLink
										isDisabled={
											!canEditObjectProperties || objectProperty.isManaged
										}
										variant="SECONDARY"
										size="SMALL"
										to={objectProperty.id}
									>
										Upravit
									</ButtonLink>
									<Button
										isDisabled={
											!canDeleteObjectProperties || objectProperty.isManaged
										}
										variant="DANGER"
										size="SMALL"
										onPress={() => onRemove(objectProperty.id)}
									>
										Smazat
									</Button>
								</>
							)}
						</td>
					</tr>
				)}
			</List>

			<Outlet />
		</Paper>
	)
}

function ObjectPropertiesSidepanel({
	filter,
	onClose,
	onSubmit,
}: {
	filter: Filter
	onClose: VoidFunction
	onSubmit: (filter: Filter) => void
}) {
	const [id, setId] = useState<string>(filter.id ?? '')
	const [name, setName] = useState<string>(filter.name ?? '')
	const [identifier, setIdentifier] = useState<string>(filter.identifier ?? '')

	return (
		<>
			<SidepanelContent className="space-y-4">
				<Input label="ID" value={id} onChange={setId}></Input>
				<Input label="Název" value={name} onChange={setName}></Input>
				<Input label="Klíč" value={identifier} onChange={setIdentifier}></Input>
			</SidepanelContent>
			<SidepanelFooter>
				<Button
					variant="PRIMARY"
					onPress={() => {
						onClose()
						onSubmit({ id, name, identifier })
					}}
				>
					Povrdit
				</Button>
				<Button
					variant="TEXT"
					size="TEXT"
					onPress={() => {
						onClose()
						onSubmit({})
					}}
				>
					Odstranit vše
				</Button>
			</SidepanelFooter>
		</>
	)
}
